// Angular Files
import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

// Other External Files
import { MonoTypeOperatorFunction, Observable, of } from 'rxjs';
import { map, tap, switchMap } from 'rxjs/operators';

// Teller Online Files
import {
    ConfigurationApiClient,
    PublicPortalSiteDto,
    UpdatePublicPortalSiteDto,
    TellerConnectionDto,
    EmailTemplateDto,
    EmailTemplateNameOptionDto,
    CreateEmailTemplateDto,
    UpdateEmailTemplateDto,
    SMSTemplateDto,
    SMSTemplateNameOptionDto,
    CreateSMSTemplateDto,
    UpdateSMSTemplateDto,
    ConfigMenuItemDto,
    CreateConfigMenuItemDto,
    UpdateConfigMenuItemDto,
    PageDto,
    CreatePageDto,
    UpdatePageDto,
    SearchTypeDto,
    CreateSearchTypeDto,
    UpdateSearchTypeDto,
    InboundRedirectSourceDto,
    CreateInboundRedirectSourceDto,
    UpdateInboundRedirectSourceDto,
    PageItemTypeDto,
    CreatePageItemTypeDto,
    UpdatePageItemTypeDto,
    AllAssetsDto,
    ReorderMenuItemsRequestDto,
    ReorderMenuItemDto
} from 'apps/admin-portal/src/app/core/api/AdminPortalApiClients';

// Teller Online Library Files
import {
    RumEvent,
    TellerOnlineRumService,
    TellerOnlineSiteMetadataService
} from 'teller-online-libraries/core';
import { TellerOnlineMessageService } from 'teller-online-libraries/shared';

@Injectable({
    providedIn: 'root'
})
export class ConfigurationService {
    // Cached site data to avoid duplicate API calls
    private _cachedSite: PublicPortalSiteModel | null = null;

    private _rum = inject(TellerOnlineRumService);

    constructor(
        private http: HttpClient,
        private configurationApiClient: ConfigurationApiClient,
        private siteMetadataService: TellerOnlineSiteMetadataService,
        private messageService: TellerOnlineMessageService
    ) { }

    // #region Helpers

    /**
     * Get the site ID for the current tenant.
     * Will fetch site data first if not already cached.
     */
    public getSiteId(): Observable<number | undefined> {
        return this.getSite().pipe(
            map(site => site?.id)
        );
    }

    /**
     * Get the cached site ID without making an API call.
     * Returns undefined if site hasn't been loaded yet.
     */
    public getCachedSiteId(): number | undefined {
        return this._cachedSite?.id;
    }

    /**
     * Clear the cached site data.
     * Useful when site data has been modified and needs to be refreshed.
     */
    public clearSiteCache(): void {
        this._cachedSite = null;
    }

    // #endregion Helpers

    // #region Site

    /**
     * Get the current site configuration for this tenant.
     * Caches the result to avoid duplicate API calls.
     * @param forceRefresh If true, bypasses the cache and fetches fresh data
     */
    public getSite(forceRefresh: boolean = false): Observable<PublicPortalSiteModel | null> {
        if (this._cachedSite && !forceRefresh) {
            return of(this._cachedSite);
        }

        return this.configurationApiClient
            .getPublicPortalSites(this.siteMetadataService.tenant)
            .pipe(
                map(response => {
                    if (response.items && response.items.length > 0) {
                        return new PublicPortalSiteModel(response.items[0]);
                    }
                    return null;
                }),
                tap(site => {
                    this._cachedSite = site;
                })
            );
    }

    /**
     * Update the site configuration.
     * Clears the cache after successful update.
     */
    public updateSite(site: PublicPortalSiteModel): Observable<PublicPortalSiteModel> {
        const updateDto = new UpdatePublicPortalSiteDto();
        updateDto.tenantName = site.tenantName;
        updateDto.title = site.title;
        updateDto.subtitle = site.subtitle;
        updateDto.homeBannerContent = site.homeBannerContent;
        updateDto.defaultCountryCode = site.defaultCountryCode;
        updateDto.defaultStateCode = site.defaultStateCode;
        updateDto.cartDisclaimer = site.cartDisclaimer;
        updateDto.multiPaymentDisclaimer = site.multiPaymentDisclaimer;
        updateDto.presentConsentInModal = site.presentConsentInModal;
        updateDto.requireSavedMethodReauthorizationCreditCard = site.requireSavedMethodReauthorizationCreditCard;
        updateDto.requireSavedMethodReauthorizationECheck = site.requireSavedMethodReauthorizationECheck;
        updateDto.maxLinkedAccountsPerUser = site.maxLinkedAccountsPerUser;
        updateDto.offerAccountLinkingAfterCheckout = site.offerAccountLinkingAfterCheckout;
        updateDto.fromEmailDisplay = site.fromEmailDisplay;
        updateDto.emailFooterHTML = site.emailFooterHTML;
        updateDto.footerLinksContent = site.footerLinksContent;

        return this.configurationApiClient.updatePublicPortalSite(site.id!, updateDto).pipe(
            map(result => new PublicPortalSiteModel(result)),
            tap(updatedSite => {
                this._cachedSite = updatedSite;
            }),
            this._saved('site')
        );
    }

    /**
     * Get all branding assets for a site.
     * @param siteId The site ID to get assets for
     */
    public getAssets(siteId: number): Observable<AllAssetsDto> {
        return this.configurationApiClient.getAllAssets(siteId);
    }

    /**
     * Upload branding assets (logo, favicon, styles) for a site.
     * @param siteId The site ID to upload assets for
     * @param logo Optional logo file (PNG)
     * @param favicon Optional favicon file (ICO)
     * @param styles Optional styles file (CSS)
     */
    public uploadAssets(
        siteId: number,
        logo?: File,
        favicon?: File,
        styles?: File
    ): Observable<AssetUploadResult> {
        const formData = new FormData();

        if (logo) {
            formData.append('logo', logo, logo.name);
        }
        if (favicon) {
            formData.append('favicon', favicon, favicon.name);
        }
        if (styles) {
            formData.append('styles', styles, styles.name);
        }

        return this.http.post<AssetUploadResult>(
            `/api/admin-portal/config/public-portal-sites/${siteId}/assets`,
            formData
        );
    }

    // #endregion Site

    // #region Teller Connections

    /**
     * Get all teller connections for the current site.
     */
    public getTellerConnections(): Observable<TellerConnectionModel[]> {
        return this.getSiteId().pipe(
            switchMap(siteId =>
                this.configurationApiClient.getTellerConnections(siteId)
            ),
            map(response =>
                (response.items || []).map(
                    item => new TellerConnectionModel(item)
                )
            )
        );
    }

    /**
     * Get the work item types configured in a Teller system, for the submit-form picker.
     * The system code comes from the TellerConnection of the page being edited — the Teller
     * feed is per-system and has no tenant-wide equivalent.
     */
    public getWorkItemTypes(tellerSystemCode: string): Observable<string[]> {
        return this.configurationApiClient.getWorkItemTypes(tellerSystemCode).pipe(
            map(response => response.workItemTypes ?? [])
        );
    }

    /**
     * Create a new teller connection.
     */
    public createTellerConnection(
        tellerConnection: TellerConnectionModel
    ): Observable<TellerConnectionModel> {
        return this.getSiteId().pipe(
            switchMap(siteId => {
                const dto = new TellerConnectionDto();
                dto.name = tellerConnection.name;
                dto.configNo = tellerConnection.configNo;
                dto.tellerSystemCode = tellerConnection.tellerSystemCode;
                dto.tellerLocationNo = tellerConnection.tellerLocationNo;
                dto.tellerPaymentChannelNo = tellerConnection.tellerPaymentChannelNo;
                dto.sessionEndTime = tellerConnection.sessionEndTime;
                dto.sessionNamePrefix = tellerConnection.sessionNamePrefix
                    || undefined;
                dto.tellerId = tellerConnection.tellerId;
                dto.publicPortalSiteId = siteId;

                // Sent on create as well as update: the server persists both collections
                // after the insert, so tender configuration entered while creating is kept
                // rather than silently dropped. tenderLimits carries availability too - a
                // row's `enabled` flag IS the payment method being offered.
                dto.tenderTypeMappings = tellerConnection.tenderTypeMappings;
                dto.tenderLimits = tellerConnection.tenderLimits;

                return this.configurationApiClient
                    .createTellerConnection(dto);
            }),
            map(result => new TellerConnectionModel(result)),
            this._saved('teller_connection')
        );
    }

    /**
     * Update an existing teller connection.
     */
    public updateTellerConnection(
        tellerConnection: TellerConnectionModel
    ): Observable<TellerConnectionModel> {
        const dto = new TellerConnectionDto();
        dto.name = tellerConnection.name;
        dto.configNo = tellerConnection.configNo;
        dto.tellerSystemCode = tellerConnection.tellerSystemCode;
        dto.tellerLocationNo = tellerConnection.tellerLocationNo;
        dto.tellerPaymentChannelNo = tellerConnection.tellerPaymentChannelNo;
        dto.sessionEndTime = tellerConnection.sessionEndTime;
        dto.sessionNamePrefix = tellerConnection.sessionNamePrefix
            || undefined;
        dto.tellerId = tellerConnection.tellerId;
        dto.publicPortalSiteId = tellerConnection.publicPortalSiteId;

        // Sent only when the caller supplied them: the server reconciles each collection
        // to exactly what arrives, so an omitted collection is left alone and an empty one
        // clears it. A caller that does not edit tender configuration must not wipe it.
        dto.tenderTypeMappings = tellerConnection.tenderTypeMappings;
        dto.tenderLimits = tellerConnection.tenderLimits;

        return this.configurationApiClient
            .updateTellerConnection(
                tellerConnection.id!, dto
            ).pipe(
                map(result => new TellerConnectionModel(result)),
                this._saved('teller_connection')
            );
    }

    /**
     * Delete a teller connection.
     */
    public deleteTellerConnection(id: number): Observable<void> {
        return this.configurationApiClient
            .deleteTellerConnection(id);
    }

    // #endregion Teller Connections

    // #region Email Templates

    /**
     * Get all email templates for the current site.
     */
    public getEmailTemplates(): Observable<EmailTemplateModel[]> {
        return this.getSiteId().pipe(
            switchMap(siteId => this.configurationApiClient.getEmailTemplates(siteId, undefined)),
            map(response => (response.items || []).map(item => new EmailTemplateModel(item)))
        );
    }

    /**
     * Get all available email template name options for dropdown selection.
     */
    public getEmailTemplateNameOptions(): Observable<EmailTemplateNameOptionModel[]> {
        return this.configurationApiClient.getEmailTemplateNameOptions().pipe(
            map(options => (options || []).map(item => new EmailTemplateNameOptionModel(item)))
        );
    }

    /**
     * Create a new email template.
     */
    public createEmailTemplate(template: EmailTemplateModel): Observable<EmailTemplateModel> {
        return this.getSiteId().pipe(
            switchMap(siteId => {
                const createDto = new CreateEmailTemplateDto();
                createDto.name = template.name;
                createDto.subject = template.subject;
                createDto.bodyHTML = template.bodyHTML;
                createDto.snippet = template.snippet;
                createDto.publicPortalSiteId = siteId;
                return this.configurationApiClient.createEmailTemplate(createDto);
            }),
            map(result => new EmailTemplateModel(result)),
            this._saved('email_template')
        );
    }

    /**
     * Update an existing email template.
     */
    public updateEmailTemplate(template: EmailTemplateModel): Observable<EmailTemplateModel> {
        const updateDto = new UpdateEmailTemplateDto();
        updateDto.name = template.name;
        updateDto.subject = template.subject;
        updateDto.bodyHTML = template.bodyHTML;
        updateDto.snippet = template.snippet;
        updateDto.publicPortalSiteId = template.publicPortalSiteId;
        return this.configurationApiClient.updateEmailTemplate(template.id!, updateDto).pipe(
            map(result => new EmailTemplateModel(result)),
            this._saved('email_template')
        );
    }

    /**
     * Delete an email template.
     */
    public deleteEmailTemplate(id: number): Observable<void> {
        return this.configurationApiClient.deleteEmailTemplate(id);
    }

    // #endregion Email Templates

    // #region SMS Templates

    /**
     * Get all SMS templates for the current site.
     */
    public getSmsTemplates(): Observable<SmsTemplateModel[]> {
        return this.getSiteId().pipe(
            switchMap(siteId => this.configurationApiClient.getSMSTemplates(siteId, undefined)),
            map(response => (response.items || []).map(item => new SmsTemplateModel(item)))
        );
    }

    /**
     * Get all available SMS template name options for dropdown selection.
     */
    public getSmsTemplateNameOptions(): Observable<SmsTemplateNameOptionModel[]> {
        return this.configurationApiClient.getSMSTemplateNameOptions().pipe(
            map(options => (options || []).map(item => new SmsTemplateNameOptionModel(item)))
        );
    }

    /**
     * Create a new SMS template.
     */
    public createSmsTemplate(template: SmsTemplateModel): Observable<SmsTemplateModel> {
        return this.getSiteId().pipe(
            switchMap(siteId => {
                const createDto = new CreateSMSTemplateDto();
                createDto.name = template.name;
                createDto.bodyTemplate = template.bodyTemplate;
                createDto.publicPortalSiteId = siteId;
                return this.configurationApiClient.createSMSTemplate(createDto);
            }),
            map(result => new SmsTemplateModel(result)),
            this._saved('sms_template')
        );
    }

    /**
     * Update an existing SMS template.
     */
    public updateSmsTemplate(template: SmsTemplateModel): Observable<SmsTemplateModel> {
        const updateDto = new UpdateSMSTemplateDto();
        updateDto.name = template.name;
        updateDto.bodyTemplate = template.bodyTemplate;
        updateDto.publicPortalSiteId = template.publicPortalSiteId;
        return this.configurationApiClient.updateSMSTemplate(template.id!, updateDto).pipe(
            map(result => new SmsTemplateModel(result)),
            this._saved('sms_template')
        );
    }

    /**
     * Delete an SMS template.
     */
    public deleteSmsTemplate(id: number): Observable<void> {
        return this.configurationApiClient.deleteSMSTemplate(id);
    }

    // #endregion SMS Templates

    // #region Menu Items

    /**
     * Get all menu items for the current site.
     */
    public getMenuItems(): Observable<MenuItemModel[]> {
        return this.getSiteId().pipe(
            switchMap(
                siteId => this.configurationApiClient.getMenuItems(siteId, undefined, undefined)
            ),
            map(response => (response.items || []).map(item => new MenuItemModel(item)))
        );
    }

    /**
     * Create a new menu item.
     */
    public createMenuItem(menuItem: MenuItemModel): Observable<MenuItemModel> {
        return this.getSiteId().pipe(
            switchMap(siteId => {
                const createDto = new CreateConfigMenuItemDto();
                createDto.configNo = menuItem.configNo;
                createDto.label = menuItem.label;
                createDto.description = menuItem.description;
                createDto.displayOrder = menuItem.displayOrder;
                createDto.active = menuItem.active;
                createDto.pageId = menuItem.pageId;
                createDto.publicPortalSiteId = siteId;
                return this.configurationApiClient.createMenuItem(createDto);
            }),
            map(result => new MenuItemModel(result)),
            this._saved('menu_item')
        );
    }

    /**
     * Update an existing menu item.
     */
    public updateMenuItem(menuItem: MenuItemModel): Observable<MenuItemModel> {
        const updateDto = new UpdateConfigMenuItemDto();
        updateDto.configNo = menuItem.configNo;
        updateDto.label = menuItem.label;
        updateDto.description = menuItem.description;
        updateDto.displayOrder = menuItem.displayOrder;
        updateDto.active = menuItem.active;
        updateDto.pageId = menuItem.pageId;
        updateDto.publicPortalSiteId = menuItem.publicPortalSiteId;
        return this.configurationApiClient.updateMenuItem(menuItem.menuItemId!, updateDto).pipe(
            map(result => new MenuItemModel(result)),
            this._saved('menu_item')
        );
    }

    /**
     * Reorder menu items by updating their display order values in a single batch operation.
     * @param items Array of menu items with their new display order values
     */
    public reorderMenuItems(items: MenuItemModel[]): Observable<MenuItemModel[]> {
        const request = new ReorderMenuItemsRequestDto();
        request.items = items.map(item => {
            const dto = new ReorderMenuItemDto();
            dto.menuItemId = item.menuItemId;
            dto.displayOrder = item.displayOrder;
            return dto;
        });
        return this.configurationApiClient.reorderMenuItems(request).pipe(
            map(results => (results || []).map(item => new MenuItemModel(item)))
        );
    }

    /**
     * Get active menu items that reference a specific page.
     * @param pageId The page ID to filter by
     */
    public getActiveMenuItemsByPageId(pageId: number): Observable<MenuItemModel[]> {
        return this.getSiteId().pipe(
            switchMap(siteId => this.configurationApiClient.getMenuItems(siteId, pageId, true)),
            map(response => (response.items || []).map(item => new MenuItemModel(item)))
        );
    }

    /**
     * Checks if a page can be inactivated by verifying no active menu items reference it.
     * If active menu items exist, shows a blocking dialog informing the user.
     * @param pageId The ID of the page to check
     * @returns Observable<boolean> - true if inactivation can proceed, false if blocked
     */
    public checkCanInactivatePage(pageId: number): Observable<boolean> {
        return this.getActiveMenuItemsByPageId(pageId).pipe(
            map((activeMenuItems: MenuItemModel[]) => {
                if (activeMenuItems.length > 0) {
                    this.showInactivationBlockedDialog(activeMenuItems);
                    return false;
                }
                return true;
            })
        );
    }

    /**
     * Shows a blocking dialog informing the user that a page cannot be inactivated
     * because it is referenced by active menu items.
     */
    private showInactivationBlockedDialog(activeMenuItems: MenuItemModel[]): void {
        const menuItemLabels = activeMenuItems.map(m => m.label).join(', ');
        const plural = activeMenuItems.length === 1 ? '' : 's';

        this.messageService.alert(
            `This page cannot be inactivated because it is referenced by the following active menu item${plural}: ${menuItemLabels}. Please inactivate the menu item${plural} first.`,
            'Cannot Inactivate Page'
        );
    }

    // #endregion Menu Items

    // #region Pages

    /**
     * Get all pages for the current site.
     */
    public getPages(): Observable<PageModel[]> {
        return this.getSiteId().pipe(
            switchMap(siteId => this.configurationApiClient.getPages(siteId, undefined, undefined)),
            map(response => (response.items || []).map(item => new PageModel(item)))
        );
    }

    /**
     * Create a new page.
     */
    public createPage(page: PageModel): Observable<PageModel> {
        return this.getSiteId().pipe(
            switchMap(siteId => {
                const createDto = new CreatePageDto();
                createDto.configNo = page.configNo;
                createDto.header = page.header;
                createDto.description = page.description;
                createDto.tellerBusinessSystemNo = page.tellerBusinessSystemNo;
                createDto.paginatedResults = page.paginatedResults;
                createDto.active = page.active;
                createDto.paymentProcessorConfigKey = page.paymentProcessorConfigKey;
                createDto.tellerConnectionId = page.tellerConnectionId;
                createDto.publicPortalSiteId = siteId;
                createDto.scheduledPaymentsEnabled = page.scheduledPaymentsEnabled;
                createDto.scheduledPaymentTellerConnectionId = page
                    .scheduledPaymentTellerConnectionId;
                createDto.scheduledPaymentTellerBusinessSystemNo = page
                    .scheduledPaymentTellerBusinessSystemNo;
                createDto.enableAccountLinking = page.enableAccountLinking;
                createDto.accountTypeName = page.accountTypeName;
                createDto.accountIconName = page.accountIconName;
                createDto.accountFetchTemplate = page.accountFetchTemplate;
                createDto.allowLinkingFromItems = page.allowLinkingFromItems;

                // As above: CreatePage persists these after the insert, so a page's
                // availability no longer waits for a second save.
                createDto.tenderOverrides = page.tenderOverrides;

                return this.configurationApiClient.createPage(createDto);
            }),
            map(result => new PageModel(result)),
            this._saved('page')
        );
    }

    /**
     * Update an existing page.
     */
    public updatePage(page: PageModel): Observable<PageModel> {
        // If publicPortalSiteId is not provided, fetch it from the site
        const siteIdSource = page.publicPortalSiteId
            ? of(page.publicPortalSiteId)
            : this.getSiteId();

        return siteIdSource.pipe(
            switchMap(siteId => {
                const updateDto = new UpdatePageDto();
                updateDto.configNo = page.configNo;
                updateDto.header = page.header;
                updateDto.description = page.description;
                updateDto.tellerBusinessSystemNo = page.tellerBusinessSystemNo;
                updateDto.paginatedResults = page.paginatedResults;
                updateDto.active = page.active;
                updateDto.tellerConnectionId = page.tellerConnectionId;
                updateDto.publicPortalSiteId = siteId;
                updateDto.scheduledPaymentsEnabled = page.scheduledPaymentsEnabled;
                updateDto.scheduledPaymentTellerConnectionId = page
                    .scheduledPaymentTellerConnectionId;
                updateDto.scheduledPaymentTellerBusinessSystemNo = page
                    .scheduledPaymentTellerBusinessSystemNo;

                // See updateTellerConnection: undefined leaves the page's overrides alone,
                // an empty array clears them.
                updateDto.tenderOverrides = page.tenderOverrides;

                updateDto.enableAccountLinking = page.enableAccountLinking;
                updateDto.accountTypeName = page.accountTypeName;
                updateDto.accountIconName = page.accountIconName;
                updateDto.accountFetchTemplate = page.accountFetchTemplate;
                updateDto.allowLinkingFromItems = page.allowLinkingFromItems;
                return this.configurationApiClient.updatePage(page.id!, updateDto);
            }),
            map(result => new PageModel(result)),
            this._saved('page')
        );
    }

    // #endregion Pages

    // #region Search Types

    /**
     * Get all search types for a specific page.
     */
    public getSearchTypes(pageId: number): Observable<SearchTypeModel[]> {
        return this.configurationApiClient.getSearchTypes(pageId, undefined).pipe(
            map(response => (response.items || []).map(item => new SearchTypeModel(item)))
        );
    }

    /**
     * Get all search types across all pages.
     */
    public getAllSearchTypes(): Observable<SearchTypeModel[]> {
        return this.configurationApiClient.getSearchTypes(undefined, undefined).pipe(
            map(response => (response.items || []).map(item => new SearchTypeModel(item)))
        );
    }

    /**
     * Create a new search type.
     */
    public createSearchType(
        searchType: SearchTypeModel,
        pageId: number
    ): Observable<SearchTypeModel> {
        const createDto = new CreateSearchTypeDto();
        createDto.configNo = searchType.configNo;
        createDto.label = searchType.label;
        createDto.tellerFetchTypeName = searchType.tellerFetchTypeName;
        createDto.instructionsTitle = searchType.instructionsTitle;
        createDto.instructionsText = searchType.instructionsText;
        createDto.instructionsImage = searchType.instructionsImage;
        createDto.instructionsImageAltText = searchType.instructionsImageAltText;
        createDto.isLookaheadSearch = searchType.isLookaheadSearch;
        createDto.lookaheadField = searchType.lookaheadField;
        createDto.active = searchType.active;
        createDto.template = searchType.template;
        createDto.pageId = pageId;
        return this.configurationApiClient.createSearchType(createDto).pipe(
            map(result => new SearchTypeModel(result))
        );
    }

    /**
     * Update an existing search type.
     */
    public updateSearchType(searchType: SearchTypeModel): Observable<SearchTypeModel> {
        const updateDto = new UpdateSearchTypeDto();
        updateDto.configNo = searchType.configNo;
        updateDto.label = searchType.label;
        updateDto.tellerFetchTypeName = searchType.tellerFetchTypeName;
        updateDto.instructionsTitle = searchType.instructionsTitle;
        updateDto.instructionsText = searchType.instructionsText;
        updateDto.instructionsImage = searchType.instructionsImage;
        updateDto.instructionsImageAltText = searchType.instructionsImageAltText;
        updateDto.isLookaheadSearch = searchType.isLookaheadSearch;
        updateDto.lookaheadField = searchType.lookaheadField;
        updateDto.active = searchType.active;
        updateDto.template = searchType.template;
        updateDto.pageId = searchType.pageId;
        return this.configurationApiClient.updateSearchType(searchType.id!, updateDto).pipe(
            map(result => new SearchTypeModel(result))
        );
    }

    /**
     * Delete a search type by ID.
     */
    public deleteSearchType(id: number): Observable<void> {
        return this.configurationApiClient.deleteSearchType(id);
    }

    // #endregion Search Types

    // #region Inbound Redirect Sources

    /**
     * Get all inbound redirect sources for a specific page.
     */
    public getInboundRedirectSources(pageId: number): Observable<InboundRedirectSourceModel[]> {
        return this.configurationApiClient.getInboundRedirectSources(pageId, undefined).pipe(
            map(response => (response.items || []).map(
                item => new InboundRedirectSourceModel(item)
            ))
        );
    }

    /**
     * Get all inbound redirect sources across all pages.
     */
    public getAllInboundRedirectSources(): Observable<InboundRedirectSourceModel[]> {
        return this.configurationApiClient.getInboundRedirectSources(undefined, undefined).pipe(
            map(response => (response.items || []).map(
                item => new InboundRedirectSourceModel(item)
            ))
        );
    }

    /**
     * Create a new inbound redirect source.
     */
    public createInboundRedirectSource(
        source: InboundRedirectSourceModel, pageId: number
    ): Observable<InboundRedirectSourceModel> {
        const createDto = new CreateInboundRedirectSourceDto();
        createDto.configNo = source.configNo;
        createDto.sourceKey = source.sourceKey;
        createDto.alias = source.alias;
        createDto.securityKey = source.securityKey;
        createDto.paymentOptionType = source.paymentOptionType;
        createDto.parameterFormat = source.parameterFormat;
        createDto.active = source.active;
        createDto.label = source.label;
        createDto.showErrorDialog = source.showErrorDialog;
        createDto.allowCartItemDetailsPage = source.allowCartItemDetailsPage;
        createDto.skipReceiptPage = source.skipReceiptPage;
        createDto.dontSendReceiptEmail = source.dontSendReceiptEmail;
        createDto.handlerType = source.handlerType;
        createDto.pluginNo = source.pluginNo;
        createDto.cancelUrl = source.cancelUrl;
        createDto.errorUrl = source.errorUrl;
        createDto.successUrl = source.successUrl;
        createDto.pageId = pageId;
        return this.configurationApiClient.createInboundRedirectSource(createDto).pipe(
            map(result => new InboundRedirectSourceModel(result))
        );
    }

    /**
     * Update an existing inbound redirect source.
     */
    public updateInboundRedirectSource(
        source: InboundRedirectSourceModel
    ): Observable<InboundRedirectSourceModel> {
        const updateDto = new UpdateInboundRedirectSourceDto();
        updateDto.configNo = source.configNo;
        updateDto.sourceKey = source.sourceKey;
        updateDto.alias = source.alias;
        updateDto.securityKey = source.securityKey;
        updateDto.paymentOptionType = source.paymentOptionType;
        updateDto.parameterFormat = source.parameterFormat;
        updateDto.active = source.active;
        updateDto.label = source.label;
        updateDto.showErrorDialog = source.showErrorDialog;
        updateDto.allowCartItemDetailsPage = source.allowCartItemDetailsPage;
        updateDto.skipReceiptPage = source.skipReceiptPage;
        updateDto.dontSendReceiptEmail = source.dontSendReceiptEmail;
        updateDto.handlerType = source.handlerType;
        updateDto.pluginNo = source.pluginNo;
        updateDto.cancelUrl = source.cancelUrl;
        updateDto.errorUrl = source.errorUrl;
        updateDto.successUrl = source.successUrl;
        updateDto.pageId = source.pageId;
        return this.configurationApiClient.updateInboundRedirectSource(source.id!, updateDto).pipe(
            map(result => new InboundRedirectSourceModel(result))
        );
    }

    // #endregion Inbound Redirect Sources

    // #region Page Item Types

    /**
     * Get all page item types for a specific page.
     */
    public getPageItemTypes(pageId: number): Observable<PageItemTypeModel[]> {
        return this.configurationApiClient.getPageItemTypes(pageId, undefined).pipe(
            map(response => (response.items || []).map(item => new PageItemTypeModel(item)))
        );
    }

    /**
     * Get all page item types across all pages.
     */
    public getAllPageItemTypes(): Observable<PageItemTypeModel[]> {
        return this.configurationApiClient.getPageItemTypes(undefined, undefined).pipe(
            map(response => (response.items || []).map(item => new PageItemTypeModel(item)))
        );
    }

    /**
     * Create a new page item type.
     */
    public createPageItemType(
        itemType: PageItemTypeModel,
        pageId: number
    ): Observable<PageItemTypeModel> {
        const createDto = new CreatePageItemTypeDto();
        createDto.configNo = itemType.configNo;
        createDto.label = itemType.label;
        createDto.image = itemType.image;
        createDto.internalNotificationEmail = itemType.internalNotificationEmail;
        createDto.internalNotificationEmailTemplate = itemType.internalNotificationEmailTemplate;
        createDto.helpText = itemType.helpText;
        createDto.active = itemType.active;
        createDto.tellerItemConfigNo = itemType.tellerItemConfigNo;
        createDto.tellerId = itemType.tellerId;
        createDto.pageId = pageId;
        createDto.isSubmitForm = itemType.isSubmitForm;
        createDto.workItemType = itemType.workItemType;
        createDto.workflowNextSteps = itemType.workflowNextSteps;
        createDto.confirmationNumberPrefix = itemType.confirmationNumberPrefix;
        return this.configurationApiClient.createPageItemType(createDto).pipe(
            map(result => new PageItemTypeModel(result))
        );
    }

    /**
     * Update an existing page item type.
     */
    public updatePageItemType(itemType: PageItemTypeModel): Observable<PageItemTypeModel> {
        const updateDto = new UpdatePageItemTypeDto();
        updateDto.configNo = itemType.configNo;
        updateDto.label = itemType.label;
        updateDto.image = itemType.image;
        updateDto.internalNotificationEmail = itemType.internalNotificationEmail;
        updateDto.internalNotificationEmailTemplate = itemType.internalNotificationEmailTemplate;
        updateDto.helpText = itemType.helpText;
        updateDto.active = itemType.active;
        updateDto.tellerItemConfigNo = itemType.tellerItemConfigNo;
        updateDto.tellerId = itemType.tellerId;
        updateDto.pageId = itemType.pageId;
        updateDto.isSubmitForm = itemType.isSubmitForm;
        updateDto.workItemType = itemType.workItemType;
        updateDto.workflowNextSteps = itemType.workflowNextSteps;
        updateDto.confirmationNumberPrefix = itemType.confirmationNumberPrefix;
        return this.configurationApiClient.updatePageItemType(itemType.id!, updateDto).pipe(
            map(result => new PageItemTypeModel(result))
        );
    }

    /**
     * Delete a page item type by ID.
     */
    public deletePageItemType(id: number): Observable<void> {
        return this.configurationApiClient.deletePageItemType(id);
    }

    // #endregion Page Item Types

    // #region Portal Features

    /**
     * Check if a specific portal feature is enabled for the current site.
     * @param featureName The name of the feature to check (e.g., "TellerOnlineLite")
     */
    public isPortalFeatureEnabled(featureName: string): Observable<boolean> {
        return this.getSiteId().pipe(
            switchMap(siteId => this.configurationApiClient.getPortalFeatures(siteId, undefined)),
            map(response => {
                const feature = response.items?.find(f => f.name === featureName);
                return feature?.enabled === true;
            })
        );
    }

    // #endregion Portal Features

    // #region Configuration Import/Export

    /**
     * Preview an import operation without persisting changes.
     * Returns detailed information about what would be changed.
     * @param siteId The site ID to preview import into
     * @param file The ZIP file containing configuration data
     */
    public previewImport(siteId: number, file: File): Observable<ConfigurationImportResult> {
        const formData = new FormData();
        formData.append('file', file, file.name);

        return this.http.post<ConfigurationImportResult>(
            `/api/admin-portal/config/public-portal-sites/${siteId}/configuration/import/preview`,
            formData
        );
    }

    /**
     * Import configuration from a ZIP file, replacing the site's configuration.
     * @param siteId The site ID to import into
     * @param file The ZIP file containing configuration data
     */
    public importConfiguration(siteId: number, file: File): Observable<ConfigurationImportResult> {
        const formData = new FormData();
        formData.append('file', file, file.name);

        return this.http.post<ConfigurationImportResult>(
            `/api/admin-portal/config/public-portal-sites/${siteId}/configuration/import`,
            formData
        );
    }

    // #endregion Configuration Import/Export

    /** Records which configuration section was saved, once the save has succeeded. */
    private _saved<T>(section: string): MonoTypeOperatorFunction<T> {
        return tap<T>(() => this._rum.recordEvent(RumEvent.adminConfigSaved, { section }));
    }
}

// #region Site Models

export class PublicPortalSiteModel extends PublicPortalSiteDto { }

// #endregion Site Models

// #region Teller Connection Models

export class TellerConnectionModel extends TellerConnectionDto {
    /** The location name (display only, not persisted) */
    tellerLocationName?: string;

    /** The payment channel name (display only, not persisted) */
    tellerPaymentChannelName?: string;
}

// #endregion Teller Connection Models

// #region Email Template Models

export class EmailTemplateModel extends EmailTemplateDto { }

export class EmailTemplateNameOptionModel extends EmailTemplateNameOptionDto { }

// #endregion Email Template Models

// #region SMS Template Models

export class SmsTemplateModel extends SMSTemplateDto { }

export class SmsTemplateNameOptionModel extends SMSTemplateNameOptionDto { }

// #endregion SMS Template Models

// #region Menu Item Models

export class MenuItemModel extends ConfigMenuItemDto { }

// #endregion Menu Item Models

// #region Page Models

export class PageModel extends PageDto {
    /** The number of search types for this page (display only, not persisted) */
    searchTypeCount?: number;

    /** The source key for the inbound redirect source (display only, not persisted) */
    inboundRedirectSourceKey?: number;

    /** The handler type for the inbound redirect source (display only, not persisted) */
    inboundRedirectHandlerType?: number;

    /** Whether the inbound redirect source is active (display only, not persisted) */
    inboundRedirectSourceActive?: boolean;

    /** The alias for the inbound redirect source (display only, not persisted) */
    inboundRedirectSourceAlias?: string;

    /** The number of page item types for this page (display only, not persisted) */
    pageItemTypeCount?: number;
}

// #endregion Page Models

// #region Search Type Models

export class SearchTypeModel extends SearchTypeDto { }

// #endregion Search Type Models

// #region Inbound Redirect Source Models

export class InboundRedirectSourceModel extends InboundRedirectSourceDto { }

// #endregion Inbound Redirect Source Models

// #region Page Item Type Models

export class PageItemTypeModel extends PageItemTypeDto { }

// #endregion Page Item Type Models

// #region Asset Upload Models

export interface AssetUploadResultItem {
    key: string;
    url: string | null;
}

export interface AssetUploadResult {
    logo?: AssetUploadResultItem;
    favicon?: AssetUploadResultItem;
    styles?: AssetUploadResultItem;
}

// #endregion Asset Upload Models

// #region Import Preview Models

export enum ChangeType {
    Create = 0,
    Update = 1,
    Unchanged = 2
}

export interface ConfigurationImportResult {
    success: boolean;
    publicPortalSiteId: number;
    summary: ConfigurationImportSummary;
    errors: string[];
    warnings: string[];
    preview?: ImportPreviewDetails;
    /** The incoming configuration from the ZIP file. Only populated during preview. */
    incomingConfiguration?: PublicPortalSiteExportDto;
    /** Information about assets included in the import ZIP. */
    assets?: ImportAssetInfo;
    /** The source environment name from the export (e.g., "Development", "Production"). */
    sourceEnvironmentName?: string;
    /** The target environment name where the import will be applied. */
    targetEnvironmentName?: string;
}

export interface ConfigurationImportSummary {
    /** Total number of property changes detected across all entities */
    totalPropertyChanges: number;
    /** Total number of asset changes detected (new or modified) */
    totalAssetChanges: number;
    /** Combined total of property and asset changes */
    totalChanges: number;

    /**
     * Dictionary of entity type names to the count of entities with changes.
     * Keys: "TellerConnections", "Pages", "SearchTypes", "PageItemTypes", "InboundRedirectSources",
     *       "MenuItems", "EmailTemplates", "SMSTemplates", "Assets"
     * Values: Number of entities of that type that have at least one property change.
     */
    entitiesWithChanges: { [key: string]: number };
}

export interface ImportPreviewDetails {
    sourceTenantName: string;
    targetTenantName: string;
    site: EntityChangePreview<SitePropertyChange>;
    tellerConnections: TellerConnectionChangePreview[];
    menuItems: EntityChangeSet<MenuItemChange>;
    emailTemplates: EntityChangeSet<EmailTemplateChange>;
    smsTemplates: EntityChangeSet<SMSTemplateChange>;
    assets: AssetChanges;
}

export interface EntityChangeSet<T> {
    toCreate: T[];
    toUpdate: T[];
}

export interface EntityChangePreview<T> {
    current: T;
    incoming: T;
}

export interface TellerConnectionChangePreview {
    changeType: ChangeType;
    existingId?: number;
    tellerSystemCode: string;
    displayName: string;
    pages: PageChangePreview[];
}

export interface PageChangePreview {
    changeType: ChangeType;
    existingId?: number;
    header: string;
    tellerBusinessSystemNo?: number;
    searchTypes: EntityChangeSet<SearchTypeChange>;
    pageItemTypes: EntityChangeSet<PageItemTypeChange>;
    inboundRedirectSources: EntityChangeSet<InboundRedirectSourceChange>;
}

export interface SitePropertyChange {
    tenantName: string;
    title: string;
    subtitle: string;
    fromEmailDisplay: string;
    defaultCountryCode: string;
    defaultStateCode: string;
}

export interface SearchTypeChange {
    existingId?: number;
    label: string;
    tellerFetchTypeName: string;
    active: boolean;
    hasImage: boolean;
}

export interface PageItemTypeChange {
    existingId?: number;
    label: string;
    tellerItemConfigNo: number;
    active: boolean;
    hasImage: boolean;
}

export interface InboundRedirectSourceChange {
    existingId?: number;
    label: string;
    sourceKey: number;
    active: boolean;
}

export interface MenuItemChange {
    existingId?: number;
    label: string;
    displayOrder: number;
    active: boolean;
    pageId?: number;
}

export interface EmailTemplateChange {
    existingId?: number;
    name: number;
    subject: string;
}

export interface SMSTemplateChange {
    existingId?: number;
    name: number;
}

export interface AssetChanges {
    hasLogo: boolean;
    hasFavicon: boolean;
    hasStyles: boolean;
    searchTypeImages: number;
    pageItemTypeImages: number;
}

// #endregion Import Preview Models

// #region Export DTO Models (for import preview comparison)

export interface ImportAssetInfo {
    hasLogo: boolean;
    hasFavicon: boolean;
    hasStyles: boolean;
    searchTypeImageCount: number;
    pageItemTypeImageCount: number;
    logoChange?: AssetChangeInfo;
    faviconChange?: AssetChangeInfo;
    stylesChange?: AssetChangeInfo;
    searchTypeImageChanges?: SearchTypeImageChange[];
    pageItemTypeImageChanges?: PageItemTypeImageChange[];
}

export interface AssetChangeInfo {
    hasIncoming: boolean;
    currentExists: boolean;
    changeType: 'none' | 'new' | 'replace' | 'unchanged';
}

export interface SearchTypeImageChange {
    searchTypeLabel: string;
    pageHeader: string;
    change: AssetChangeInfo;
}

export interface PageItemTypeImageChange {
    pageItemTypeLabel: string;
    pageHeader: string;
    change: AssetChangeInfo;
}

/**
 * Constants for asset change type values used in import comparison.
 */
export const AssetChangeType = {
    /** No incoming asset in the import file. */
    None: 'none',
    /** Incoming asset exists, no current asset - will be added. */
    New: 'new',
    /** Incoming asset differs from current - will be replaced. */
    Replace: 'replace',
    /** Incoming asset is identical to current - no action needed. */
    Unchanged: 'unchanged'
} as const;

export type AssetChangeTypeValue = typeof AssetChangeType[keyof typeof AssetChangeType];

export interface PublicPortalSiteExportDto {
    tenantName: string;
    title: string;
    subtitle: string;
    footerLinksContent: string;
    homeBannerContent: string;
    fromEmailDisplay: string;
    emailFooterHTML: string;
    cartDisclaimer: string;
    multiPaymentDisclaimer: string;
    defaultCountryCode: string;
    defaultStateCode: string;
    logoAssetPath?: string;
    faviconAssetPath?: string;
    stylesAssetPath?: string;
    tellerConnections: TellerConnectionExportDto[];
    portalFeatures: PortalFeatureExportDto[];
    emailTemplates: EmailTemplateExportDto[];
    smsTemplates: SMSTemplateExportDto[];
    menuItems: MenuItemExportDto[];
    terminology: TerminologyExportDto[];
}

export interface TellerConnectionExportDto {
    configNo: number;
    name: string;
    tellerSystemCode: string;
    tellerLocationNo: number;
    tellerPaymentChannelNo?: number;
    tellerSessionDateFormat: string;
    sessionNamePrefix: string;
    sessionEndTime: string;
    paymentProcessorConfigKey?: string;
    pages: PageExportDto[];
}

export interface PageExportDto {
    configNo: number;
    tellerBusinessSystemNo?: number;
    header: string;
    description: string;
    paginatedResults: boolean;
    active: boolean;
    paymentProcessorConfigKey?: string;
    scheduledPaymentsEnabled?: boolean;
    scheduledPaymentTellerConnectionId?: number;
    scheduledPaymentTellerBusinessSystemNo?: number;
    enableAccountLinking: boolean;
    accountTypeName?: string;
    accountIconName?: string;
    accountFetchTemplate?: string;
    allowLinkingFromItems?: boolean;
    searchTypes: SearchTypeExportDto[];
    pageItemTypes: PageItemTypeExportDto[];
    inboundRedirectSources: InboundRedirectSourceExportDto[];
}

export interface SearchTypeExportDto {
    configNo: number;
    tellerFetchTypeName: string;
    label: string;
    instructionsTitle: string;
    instructionsText: string;
    instructionsImageAssetPath?: string;
    instructionsImageAltText: string;
    active: boolean;
    template: string;
    isLookaheadSearch: boolean;
    lookaheadField: string;
    flow: number;
    loadMoreIncrement: number;
}

export interface PageItemTypeExportDto {
    configNo: number;
    label: string;
    imageAssetPath?: string;
    internalNotificationEmail: string;
    internalNotificationEmailTemplate: string;
    helpText: string;
    workflowNextSteps?: string;
    confirmationNumberPrefix?: string;
    active: boolean;
    tellerItemConfigNo: number;
}

export interface InboundRedirectSourceExportDto {
    configNo: number;
    sourceKey: number;
    securityKey: string;
    paymentOptionType: string;
    active: boolean;
    parameterFormat: number;
    label: string;
    showErrorDialog: boolean;
    allowCartItemDetailsPage: boolean;
    skipReceiptPage: boolean;
    dontSendReceiptEmail: boolean;
    handlerType: number;
    pluginNo?: number;
    cancelUrl: string;
    errorUrl: string;
    successUrl: string;
}

export interface MenuItemExportDto {
    configNo: number;
    label: string;
    description: string;
    displayOrder: number;
    active: boolean;
    pageConfigNo?: number;
}

export interface PortalFeatureExportDto {
    name: string;
    enabled: boolean;
}

export interface EmailTemplateExportDto {
    name: number;
    subject: string;
    bodyHTML: string;
}

export interface SMSTemplateExportDto {
    name: number;
    bodyTemplate: string;
}

export interface TerminologyExportDto {
    name: string;
    singular: string;
    plural?: string;
}

// #endregion Export DTO Models
